import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { keyToDisplayString, displayStringToKey } from '../../utils/StringUtils';
import { get_cat_breeds, get_dog_breeds } from '../../utils/BreedUtils';
import { get_us_states, get_timezone_for_state, get_timezone_display } from '../../utils/StateUtils';
import Button from '../../components/ui/Button/Button';
import Card from '../../components/ui/Card/Card';
import Popover from '../../components/ui/Popover/Popover';
import DownloadMenu from '../../components/ui/DownloadMenu/DownloadMenu';
import { getParticipants, updateParticipant, exportAllParticipants, deleteParticipants } from '../../controllers/participants';
import { getIncubation, updateFieldPositions } from '../../controllers/incubations';
import { getOrganizations } from '../../controllers/organizations';
import { getUsers } from '../../controllers/users';
import { getIncubationEngagements, getExternalChatMessages, exportIncubationEngagements } from '../../controllers/external';
import { useToast } from '../../contexts/ToastContext';
import { useUser } from '../../contexts/UserContext';
import './IncubationScreen.css';

const rowsToCsv = (rows) => {
  if (rows.length === 0) return '';
  const headers = Object.keys(rows[0]);
  const escape = (val) => `"${String(val ?? '').replace(/"/g, '""')}"`;
  const lines = [headers.map(escape).join(',')];
  for (const row of rows) {
    lines.push(headers.map(h => escape(row[h])).join(','));
  }
  return lines.join('\n');
};

const triggerFileDownload = (content, filename, mimeType) => {
  const blob = new Blob([content], { type: mimeType });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const downloadRowsAs = (rows, baseName, format) => {
  if (format === 'json') {
    triggerFileDownload(JSON.stringify(rows, null, 2), `${baseName}.json`, 'application/json');
  } else {
    triggerFileDownload(rowsToCsv(rows), `${baseName}.csv`, 'text/csv;charset=utf-8;');
  }
};
 


const IncubationScreen = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();
  const { hasGlobalFunctionality, hasIncubationPermission, hasIncubationAccess, isDashboardAdmin } = useUser();
  const id = location.state?.incubationId;
  const [currentPage, setCurrentPage]   = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [draggedColumn, setDraggedColumn] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [showEditModal, setShowEditModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [incubation, setIncubation] = useState(null);
  const [incubationLoading, setIncubationLoading] = useState(true);
  const [columnOrder, setColumnOrder] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [organizationsLoading, setOrganizationsLoading] = useState(true);
  const [dashboardUsers, setDashboardUsers] = useState([]);
  const [dashboardUsersLoading, setDashboardUsersLoading] = useState(true);
  const [lockedHoverRow, setLockedHoverRow] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);
  const [updateTimeout, setUpdateTimeout] = useState(null);
  const [showConfigPanel, setShowConfigPanel] = useState(false);
  const [sortOrder, setSortOrder] = useState('asc');
  const [sortColumn, setSortColumn] = useState('');
  const [filterColumn, setFilterColumn] = useState('');
  const [filterValue, setFilterValue] = useState('');
  const [currentSortField, setCurrentSortField] = useState(null);
  const [currentSortDirection, setCurrentSortDirection] = useState(null);
  const [currentFilterField, setCurrentFilterField] = useState(null);
  const [currentFilterValue, setCurrentFilterValue] = useState(null);
  const isUpdatingFieldPositions = useRef(false);
  const normalizeOrgName = (value) => (value || '').trim().toLowerCase();

  // Engagements table state
  const [allEngagements, setAllEngagements] = useState([]);
  const [engagementsPage, setEngagementsPage] = useState(1);
  const [engagementsItemsPerPage, setEngagementsItemsPerPage] = useState(10);
  const [engagementsPagination, setEngagementsPagination] = useState({});
  const [engagementsLoading, setEngagementsLoading] = useState(false);
  const [engagementsExporting, setEngagementsExporting] = useState(false);
  const [selectedEngagement, setSelectedEngagement] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [showChatModal, setShowChatModal] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);

  // Delete mode state
  const [deleteMode, setDeleteMode] = useState(false);
  const [selectedParticipants, setSelectedParticipants] = useState(new Set());
  const [lastCheckedIndex, setLastCheckedIndex] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchIncubationData();
  }, [id, currentPage, itemsPerPage]);

  useEffect(() => {
    const loadOrganizations = async () => {
      try {
        const response = await getOrganizations({ page: 1, limit: 1000 });
        if (response.data && response.data.organizations) {
          setOrganizations(response.data.organizations);
        }
      } catch (error) {
        console.error('Failed to fetch organizations:', error);
        showError('Failed to load organizations. Please try again.');
      } finally {
        setOrganizationsLoading(false);
      }
    };

    loadOrganizations();
  }, [showError]);

  useEffect(() => {
    const loadDashboardUsers = async () => {
      try {
        const response = await getUsers({ page: 1, limit: 1000 });
        if (response.data && response.data.success) {
          setDashboardUsers(response.data.users || []);
        }
      } catch (error) {
        console.error('Failed to fetch dashboard users:', error);
        showError('Failed to load dashboard users. Please try again.');
      } finally {
        setDashboardUsersLoading(false);
      }
    };

    loadDashboardUsers();
  }, [showError]);

  // Load participants when incubation data is first available or pagination changes
  useEffect(() => {
    if (incubation && !isUpdatingFieldPositions.current) {
      fetchParticipants(currentSortField, currentSortDirection, currentFilterField, currentFilterValue);
    }
  }, [incubation, currentPage, itemsPerPage]);

  // Refetch participants when returning from AddParticipantsScreen
  useEffect(() => {
    if (location.state?.refreshParticipants) {
      fetchParticipants(currentSortField, currentSortDirection, currentFilterField, currentFilterValue);
      
      // Show success message if present
      if (location.state?.successMessage) {
        showSuccess(location.state.successMessage);
      }
    }
  }, [location.state]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (updateTimeout) {
        clearTimeout(updateTimeout);
      }
    };
  }, [updateTimeout]);

  // Fetch engagements for all participants
  useEffect(() => {
    const fetchEngagements = async () => {
      if (!incubation?.id) return;

      setEngagementsLoading(true);
      try {
        const response = await getIncubationEngagements({
          incubation_id: incubation.id,
          page: engagementsPage,
          limit: engagementsItemsPerPage
        });

        if (response.data?.success && response.data?.data) {
          setAllEngagements(response.data.data.engagements || []);
          setEngagementsPagination(response.data.data.pagination || {});
        }
      } catch (error) {
        console.error('Error fetching engagements:', error);
      } finally {
        setEngagementsLoading(false);
      }
    };

    fetchEngagements();
  }, [incubation?.id, engagementsPage, engagementsItemsPerPage]);

  // Handle engagement row click to show chat history
  const handleEngagementRowClick = async (engagement) => {
    const type = (engagement.type || '').toUpperCase();
    if (type !== 'CHAT' && type !== 'THREAD') return;

    setSelectedEngagement(engagement);
    setChatLoading(true);
    setShowChatModal(true);

    try {
      const response = await getExternalChatMessages({
        care_consultation_id: engagement._id || engagement.e_id
      });
      if (response.data?.success) {
        setChatMessages(response.data.data?.chat_messages || []);
      }
    } catch (error) {
      console.error('Error fetching chat messages:', error);
    } finally {
      setChatLoading(false);
    }
  };

  // Handle download engagements (CSV or JSON)
  const handleDownloadEngagements = async (format) => {
    if (!incubation || engagementsExporting) return;

    setEngagementsExporting(true);
    try {
      const response = await exportIncubationEngagements({ incubation_id: incubation.id });

      if (response.data?.success && response.data?.data?.engagements) {
        const engagements = response.data.data.engagements;

        const rows = engagements.map(engagement => ({
          'Email': engagement.email || '',
          'Engagement': formatEngagementText(engagement.subject || engagement.category || 'N/A'),
          'Pet Name': engagement.patient?.name || 'Unknown',
          'Engagement Type': formatConsultationType(engagement.type || 'N/A'),
          'Date': engagement.created_at ? new Date(engagement.created_at).toLocaleDateString() : ''
        }));

        downloadRowsAs(rows, `${incubation.name || 'incubation'}-engagements`, format);
        showSuccess(`Engagements ${format.toUpperCase()} downloaded successfully`);
      } else {
        showError(response.data?.error || 'Failed to export engagements');
      }
    } catch (error) {
      console.error('Error exporting engagements:', error);
      showError('Failed to export engagements. Please try again.');
    } finally {
      setEngagementsExporting(false);
    }
  };

  // Format engagement text: "general_advice" -> "General Advice"
  const formatEngagementText = (text) => {
    if (!text || typeof text !== 'string') return 'N/A';
    return text
      .replace(/[_-]+/g, ' ')
      .split(' ')
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');
  };

  // Format consultation type
  const formatConsultationType = (type) => {
    if (!type || typeof type !== 'string') return 'N/A';
    const upper = type.toUpperCase();
    if (upper === 'CHAT' || upper === 'THREAD') return 'Chat';
    if (upper === 'VIDEO') return 'Video';
    return formatEngagementText(type);
  };

  const fetchIncubationData = async () => {
    try {
      setIncubationLoading(true);
      const response = await getIncubation({ 
        incubation_id: id,
        page: currentPage,
        limit: itemsPerPage
      });
      
      if (response.data && response.data.success) {
        const incubationData = response.data.incubation;
        setIncubation(incubationData);
        generateColumns(incubationData);
      } else {
        setIncubation(null);
      }
    } catch (error) {
      setIncubation(null);
    } finally {
      setIncubationLoading(false);
    }
  };

  const generateColumns = (incubationData) => {
    const columns = [
      { key: 'pet_id', label: 'Pet ID', locked: true },
      { key: 'pet_incubation_id', label: 'Pet Incubation ID', locked: true }
    ];

    // Collect fields by type to control ordering
    const dataFields = [];
    const calculatedFields = [];
    const inclusionCriteria = [];
    const exclusionCriteria = [];

    // Add data_fields with their positions - use display_name if available
    if (incubationData.data_fields && Array.isArray(incubationData.data_fields)) {
      incubationData.data_fields.forEach(field => {
        dataFields.push({
          type: 'data_field',
          key: `data_${field.name}`,
          label: field.display_name || keyToDisplayString(field.name), // Use display_name if available, otherwise convert key
          position: field.display_position || 999, // Default to end if no position
          fieldType: field.type, // Store the field type
          choices: field.choices // Store the choices if it's a choices field
        });
      });
    }

    // Add calculated_fields with their positions - use display_name if available
    if (incubationData.calculated_fields && Array.isArray(incubationData.calculated_fields)) {
      incubationData.calculated_fields.forEach(field => {
        calculatedFields.push({
          type: 'calculated_field',
          key: `calculated_${field.name}`,
          label: field.display_name || keyToDisplayString(field.name), // Use display_name if available, otherwise convert key
          position: field.display_position || 999, // Default to end if no position
          fieldType: field.type, // Store the field type (number, percentage, etc.)
          sourceFieldName: field.source_field_name, // Store the source field name
          calculationType: field.calculation_type || 'pet_age' // Store the calculation type
        });
      });
    }

    // Add inclusion_criteria with their positions - convert keys to display names
    if (incubationData.inclusion_criteria && Array.isArray(incubationData.inclusion_criteria)) {
      incubationData.inclusion_criteria.forEach(criteria => {
        const criteriaName = typeof criteria === 'string' ? criteria : criteria.name;
        inclusionCriteria.push({
          type: 'inclusion_criteria',
          key: `criteria_${criteriaName}`,
          label: keyToDisplayString(criteriaName), // Convert key to display name
          position: criteria.display_position || 999 // Default to end if no position
        });
      });
    }

    // Add exclusion_criteria with their positions - convert keys to display names
    if (incubationData.exclusion_criteria && Array.isArray(incubationData.exclusion_criteria)) {
      incubationData.exclusion_criteria.forEach(criteria => {
        const criteriaName = typeof criteria === 'string' ? criteria : criteria.name;
        exclusionCriteria.push({
          type: 'exclusion_criteria',
          key: `exclusion_${criteriaName}`,
          label: keyToDisplayString(criteriaName), // Convert key to display name
          position: criteria.display_position || 999 // Default to end if no position
        });
      });
    }

    // Combine data fields and calculated fields, then sort by display_position
    // This allows calculated fields to appear in their correct positions relative to data fields
    const allFields = [...dataFields, ...calculatedFields];
    allFields.sort((a, b) => a.position - b.position);
    
    // Sort criteria separately (they remain at the end as locked columns)
    inclusionCriteria.sort((a, b) => a.position - b.position);
    exclusionCriteria.sort((a, b) => a.position - b.position);

    // Add fields to columns in the desired order:
    // 1. Data fields and calculated fields (interleaved by display_position), 2. Inclusion criteria, 3. Exclusion criteria
    [...allFields, ...inclusionCriteria, ...exclusionCriteria].forEach(field => {
      columns.push({
        key: field.key,
        label: field.label,
        locked: field.type === 'inclusion_criteria' || field.type === 'exclusion_criteria', // Lock criteria columns
        fieldType: field.fieldType,
        choices: field.choices,
        sourceFieldName: field.sourceFieldName, // Include source field name for calculated fields
        calculationType: field.calculationType // Include calculation type for calculated fields
      });
    });

    // Add Eligible column after criteria columns (only if there are inclusion or exclusion criteria)
    if (inclusionCriteria.length > 0 || exclusionCriteria.length > 0) {
      columns.push({
        key: 'eligible',
        label: 'Eligible',
        locked: true,
        fieldType: 'yes_no',
        isEligibleColumn: true
      });
    }

    // Add spacer column to fill remaining width
    columns.push({
      key: 'spacer',
      label: '',
      locked: false,
      isSpacer: true
    });

    setColumnOrder(columns);
  };

  const fetchParticipants = async (sortField = null, sortDirection = null, filterField = null, filterValue = null) => {
    // Skip loading if we're updating field positions
    if (isUpdatingFieldPositions.current) {
      return;
    }
    
    try {
      setLoading(true);
      const requestData = { 
        incubation_id: id, 
        page: currentPage, 
        limit: itemsPerPage 
      };
      
      // Add sorting parameters if provided
      if (sortField && sortDirection) {
        requestData.sort_field = sortField;
        requestData.sort_direction = sortDirection;
      }
      
      // Add filtering parameters if provided
      if (filterField && filterValue) {
        requestData.filter_field = filterField;
        requestData.filter_value = filterValue;
      }
      
      const response = await getParticipants(requestData);
      
      if (response.data && response.data.success) {
        setParticipants(response.data.participants || []);
        setHasMore(response.data.pagination?.has_next || false);
        setHasPrevious(response.data.pagination?.has_previous || false);
      } else {
        setParticipants([]);
        setHasMore(false);
        setHasPrevious(false);
      }
    } catch (error) {
      setParticipants([]);
      setHasMore(false);
      setHasPrevious(false);
    } finally {
      setLoading(false);
    }
  };

  const handleBackClick = () => {
    navigate('/home');
  };

  const handleWebhookInstructionsClick = () => {
    navigate('/webhook-instructions', { state: { incubation } });
  };

  const handleSurveysClick = () => {
    navigate('/incubation-surveys', { state: { incubation, incubationId: id } });
  };

  const handleAddParticipantsClick = () => {
    navigate('/add-participants', { state: { incubationId: id } });
  };

  const handleAddParticipantsCsvClick = () => {
    navigate('/add-participants-csv', { state: { incubationId: id } });
  };

  const handleLogsClick = () => {
    navigate('/incubation-logs', { state: { incubationId: id } });
  };

  const handleDownloadParticipants = async (format) => {
    if (!incubation || exporting) return;

    setExporting(true);
    try {
      const response = await exportAllParticipants({ incubation_id: id });

      if (response.data && response.data.success) {
        const allParticipants = response.data.participants;

        // Build column headers based on incubation field definitions
        const headers = ['pet_id', 'pet_incubation_id'];
        const fieldKeys = ['pet_id', 'pet_incubation_id'];

        const sortedDataFields = [...(incubation.data_fields || [])].sort((a, b) =>
          (a.display_position || 0) - (b.display_position || 0)
        );
        sortedDataFields.forEach(field => {
          headers.push(field.display_name || keyToDisplayString(field.name));
          fieldKeys.push({ type: 'data', name: field.name });
        });

        const sortedCalcFields = [...(incubation.calculated_fields || [])].sort((a, b) =>
          (a.display_position || 0) - (b.display_position || 0)
        );
        sortedCalcFields.forEach(field => {
          headers.push(field.display_name || keyToDisplayString(field.name));
          fieldKeys.push({ type: 'calculated', name: field.name });
        });

        const sortedInclusion = [...(incubation.inclusion_criteria || [])].sort((a, b) =>
          (a.display_position || 0) - (b.display_position || 0)
        );
        sortedInclusion.forEach(field => {
          headers.push(field.display_name || keyToDisplayString(field.name));
          fieldKeys.push({ type: 'inclusion', name: field.name });
        });

        const sortedExclusion = [...(incubation.exclusion_criteria || [])].sort((a, b) =>
          (a.display_position || 0) - (b.display_position || 0)
        );
        sortedExclusion.forEach(field => {
          headers.push(field.display_name || keyToDisplayString(field.name));
          fieldKeys.push({ type: 'exclusion', name: field.name });
        });

        const rows = allParticipants.map(participant => {
          const row = {};
          fieldKeys.forEach((key, i) => {
            const header = headers[i];
            let value = '';
            if (key === 'pet_id') {
              value = participant.pet_id || '';
            } else if (key === 'pet_incubation_id') {
              value = participant.pet_incubation_id || '';
            } else if (key.type === 'data') {
              value = participant.incubation_fields?.[key.name] ?? '';
            } else if (key.type === 'calculated') {
              value = participant.calculated_fields?.[key.name] ?? '';
            } else if (key.type === 'inclusion') {
              const val = participant.incubation_criteria_fields?.[key.name];
              value = val === true ? 'Yes' : val === false ? 'No' : '';
            } else if (key.type === 'exclusion') {
              const val = participant.incubation_exclusion_fields?.[key.name];
              value = val === true ? 'Yes' : val === false ? 'No' : '';
            }
            row[header] = value;
          });
          return row;
        });

        downloadRowsAs(rows, `${incubation.name || 'incubation'}-participants`, format);
        showSuccess(`${format.toUpperCase()} downloaded successfully`);
      } else {
        showError(response.data?.error || 'Failed to export participants');
      }
    } catch (error) {
      console.error('Error exporting participants:', error);
      showError('Failed to export participants. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // Delete mode handlers
  const handleEnterDeleteMode = () => {
    setDeleteMode(true);
    setSelectedParticipants(new Set());
    setLastCheckedIndex(null);
  };

  const handleCancelDeleteMode = () => {
    setDeleteMode(false);
    setSelectedParticipants(new Set());
    setLastCheckedIndex(null);
  };

  const handleCheckboxChange = (participantId, rowIndex, event) => {
    const isShiftKey = event.shiftKey;

    setSelectedParticipants(prev => {
      const newSelected = new Set(prev);

      if (isShiftKey && lastCheckedIndex !== null) {
        // Shift-click: select all rows between lastCheckedIndex and current rowIndex
        const start = Math.min(lastCheckedIndex, rowIndex);
        const end = Math.max(lastCheckedIndex, rowIndex);

        for (let i = start; i <= end; i++) {
          const participant = participantsWithDynamicId[i];
          if (participant) {
            newSelected.add(participant.id);
          }
        }
      } else {
        // Normal click: toggle this participant
        if (newSelected.has(participantId)) {
          newSelected.delete(participantId);
        } else {
          newSelected.add(participantId);
        }
      }

      return newSelected;
    });

    setLastCheckedIndex(rowIndex);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      // Select all participants on current page
      const allIds = new Set(participantsWithDynamicId.map(p => p.id));
      setSelectedParticipants(allIds);
    } else {
      // Deselect all
      setSelectedParticipants(new Set());
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedParticipants.size === 0) return;

    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${selectedParticipants.size} participant(s)? This action cannot be undone.`
    );

    if (!confirmDelete) return;

    try {
      setDeleting(true);

      const response = await deleteParticipants({
        participant_ids: Array.from(selectedParticipants),
        incubation_id: id
      });

      if (response.data?.success) {
        showSuccess(response.data.message || `Successfully deleted ${selectedParticipants.size} participant(s)`);

        // Exit delete mode and refresh participants
        setDeleteMode(false);
        setSelectedParticipants(new Set());
        setLastCheckedIndex(null);

        // Refresh the participants list
        await fetchParticipants(currentSortField, currentSortDirection, currentFilterField, currentFilterValue);
      } else {
        showError(response.data?.error || 'Failed to delete participants');
      }
    } catch (error) {
      console.error('Error deleting participants:', error);
      showError('Failed to delete participants. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleVersionsClick = () => {
    navigate('/version-review', { 
      state: { 
        tableName: 'incubations', 
        elementId: id, 
        elementName: incubation?.name || 'Incubation',
        backRoute: `/incubation?id=${id}`
      } 
    });
  };

  const handleViewDetailsClick = () => {
    navigate('/edit-incubation', { state: { mode: 'edit', incubationId: id } });
  };

  const handleConfigClick = () => {
    setShowConfigPanel(!showConfigPanel);
  };

  const handleSortSubmit = async () => {
    if (!sortColumn && !(filterColumn && filterValue.trim())) return;
    
    try {
      // Reset to page 1 when sorting/filtering
      setCurrentPage(1);
      
      // Convert sort order to backend format
      const sortDirection = sortOrder === 'asc' ? 'ASC' : 'DESC';
      
      // Map column keys to backend field names for sorting
      let sortField = sortColumn;
      if (sortColumn.startsWith('data_')) {
        sortField = sortColumn.replace('data_', '');
      } else if (sortColumn.startsWith('criteria_')) {
        sortField = sortColumn.replace('criteria_', '');
      }
      
      // Map column keys to backend field names for filtering
      let mappedFilterField = null;
      let mappedFilterValue = null;
      if (filterColumn && filterValue.trim()) {
        mappedFilterField = filterColumn;
        if (filterColumn.startsWith('data_')) {
          mappedFilterField = filterColumn.replace('data_', '');
        } else if (filterColumn.startsWith('criteria_')) {
          mappedFilterField = filterColumn.replace('criteria_', '');
        }
        mappedFilterValue = filterValue.trim();
      }
      
      // Store current parameters
      setCurrentSortField(sortField);
      setCurrentSortDirection(sortDirection);
      setCurrentFilterField(mappedFilterField);
      setCurrentFilterValue(mappedFilterValue);
      
      // Fetch participants with sorting and filtering parameters
      await fetchParticipants(sortField, sortDirection, mappedFilterField, mappedFilterValue);
      
      // Keep the config panel visible after applying
      // setShowConfigPanel(false);
    } catch (error) {
      // You might want to show an error message to the user here
    }
  };

  const handleClear = async () => {
    // Reset form fields
    setSortOrder('asc');
    setSortColumn('');
    setFilterColumn('');
    setFilterValue('');
    
    // Clear current parameters
    setCurrentSortField(null);
    setCurrentSortDirection(null);
    setCurrentFilterField(null);
    setCurrentFilterValue(null);
    
    // Hide the configuration panel
    setShowConfigPanel(false);
    
    // Reset to page 1 and fetch unfiltered, unsorted results
    setCurrentPage(1);
    await fetchParticipants();
  };



  const handlePreviousPage = () => {
    if (hasPrevious) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
    }
  };

  const handleNextPage = () => {
    if (hasMore) {
      const newPage = currentPage + 1;
      setCurrentPage(newPage);
    }
  };

  const handleItemsPerPageChange = async (event) => {
    const newItemsPerPage = parseInt(event.target.value);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
    
    // Make new API calls with the updated items per page
    try {
      setIncubationLoading(true);
      const response = await getIncubation({ 
        incubation_id: id,
        page: 1, // Reset to first page
        limit: newItemsPerPage
      });
      
      if (response.data && response.data.success) {
        const incubationData = response.data.incubation;
        setIncubation(incubationData);
        generateColumns(incubationData);
        
        // Fetch participants with new limit
        await fetchParticipants(currentSortField, currentSortDirection, currentFilterField, currentFilterValue);
      }
    } catch (error) {
    } finally {
      setIncubationLoading(false);
      setLoading(false);
    }
  };


  // Drag and drop handlers
  const handleDragStart = (e, columnIndex) => {
    if (columnOrder[columnIndex].locked) return;
    setDraggedColumn(columnIndex);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
    e.target.classList.add('dragging');
  };

  const handleDragOver = (e, columnIndex) => {
    e.preventDefault();
    if (columnOrder[columnIndex].locked) return;
    e.dataTransfer.dropEffect = 'move';
    
    // Add visual feedback for drop zone
    const targetTh = e.currentTarget;
    if (targetTh && !targetTh.classList.contains('drag-over')) {
      targetTh.classList.add('drag-over');
    }
  };

  const handleDragLeave = (e, columnIndex) => {
    // Remove visual feedback when leaving drop zone
    const targetTh = e.currentTarget;
    if (targetTh) {
      targetTh.classList.remove('drag-over');
    }
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedColumn === null || columnOrder[dropIndex].locked) return;
    
    // Remove all drag-over classes
    document.querySelectorAll('.drag-over').forEach(el => {
      el.classList.remove('drag-over');
    });
    
    const newColumnOrder = [...columnOrder];
    const draggedItem = newColumnOrder[draggedColumn];
    
    // Remove dragged item from original position
    newColumnOrder.splice(draggedColumn, 1);
    // Insert at new position
    newColumnOrder.splice(dropIndex, 0, draggedItem);
    
    setColumnOrder(newColumnOrder);
    setDraggedColumn(null);
    
    // Debounce API calls to prevent multiple rapid updates
    if (updateTimeout) {
      clearTimeout(updateTimeout);
    }
    
    const timeout = setTimeout(() => {
      updateFieldPositionsAPI(newColumnOrder);
    }, 500); // 500ms delay
    
    setUpdateTimeout(timeout);
  };

  const handleDragEnd = (e) => {
    // Remove all drag-related classes
    e.target.classList.remove('dragging');
    document.querySelectorAll('.drag-over').forEach(el => {
      el.classList.remove('drag-over');
    });
    setDraggedColumn(null);
  };

  // Function to update field positions via API
  const updateFieldPositionsAPI = async (newColumnOrder) => {
    if (!incubation) return;

    try {
      isUpdatingFieldPositions.current = true;
      // Process columns in their new order - maintain the actual drag-and-drop sequence
      const orderedFields = [];
      
      newColumnOrder.forEach((column, index) => {
        if (column.key.startsWith('data_')) {
          const fieldName = column.key.replace('data_', '');
          const originalField = incubation.data_fields?.find(field => field.name === fieldName);
          if (originalField) {
            orderedFields.push({
              ...originalField,
              display_position: index + 1 // Use actual column position including locked columns
            });
          }
        } else if (column.key.startsWith('criteria_')) {
          const criteriaName = column.key.replace('criteria_', '');
          const originalCriteria = incubation.inclusion_criteria?.find(criteria => {
            const name = typeof criteria === 'string' ? criteria : criteria.name;
            return name === criteriaName;
          });
          if (originalCriteria) {
            const criteriaObj = typeof originalCriteria === 'string' 
              ? { name: originalCriteria } 
              : originalCriteria;
            orderedFields.push({
              ...criteriaObj,
              display_position: index + 1 // Use actual column position including locked columns
            });
          }
        } else if (column.key.startsWith('exclusion_')) {
          const criteriaName = column.key.replace('exclusion_', '');
          const originalCriteria = incubation.exclusion_criteria?.find(criteria => {
            const name = typeof criteria === 'string' ? criteria : criteria.name;
            return name === criteriaName;
          });
          if (originalCriteria) {
            const criteriaObj = typeof originalCriteria === 'string' 
              ? { name: originalCriteria } 
              : originalCriteria;
            orderedFields.push({
              ...criteriaObj,
              display_position: index + 1 // Use actual column position including locked columns
            });
          }
        } else if (column.key.startsWith('calculated_')) {
          const fieldName = column.key.replace('calculated_', '');
          const originalField = incubation.calculated_fields?.find(field => field.name === fieldName);
          if (originalField) {
            orderedFields.push({
              ...originalField,
              display_position: index + 1 // Use actual column position including locked columns
            });
          }
        }
      });

      // Separate into data_fields, inclusion_criteria, exclusion_criteria, and calculated_fields arrays but maintain order
      const dataFields = [];
      const inclusionCriteria = [];
      const exclusionCriteria = [];
      const calculatedFields = [];
      
      orderedFields.forEach(field => {
        if (field.name && field.display_position) {
          // Check if it's a data field
          const isDataField = incubation.data_fields?.some(df => df.name === field.name);
          // Check if it's a calculated field
          const isCalculatedField = incubation.calculated_fields?.some(cf => cf.name === field.name);
          // Check if it's an inclusion criteria
          const isInclusionCriteria = incubation.inclusion_criteria?.some(ic => {
            const name = typeof ic === 'string' ? ic : ic.name;
            return name === field.name;
          });
          // Check if it's an exclusion criteria
          const isExclusionCriteria = incubation.exclusion_criteria?.some(ec => {
            const name = typeof ec === 'string' ? ec : ec.name;
            return name === field.name;
          });
          
          if (isDataField) {
            dataFields.push(field);
          } else if (isCalculatedField) {
            calculatedFields.push(field);
          } else if (isInclusionCriteria) {
            inclusionCriteria.push(field);
          } else if (isExclusionCriteria) {
            exclusionCriteria.push(field);
          }
        }
      });

      // Prepare the API request data
      const requestData = {
        incubation_id: incubation.id
      };

      if (dataFields.length > 0) {
        requestData.data_fields = dataFields;
      }
      if (inclusionCriteria.length > 0) {
        requestData.inclusion_criteria = inclusionCriteria;
      }
      if (exclusionCriteria.length > 0) {
        requestData.exclusion_criteria = exclusionCriteria;
      }
      if (calculatedFields.length > 0) {
        requestData.calculated_fields = calculatedFields;
      }

      
      const response = await updateFieldPositions(requestData);
      
      if (response.data && response.data.success) {
        // Update local state with the complete incubation object returned from API
        setIncubation(response.data.incubation);
        // Regenerate columns with the updated data
        generateColumns(response.data.incubation);
        // Reset flag after state update to prevent participant reload
        setTimeout(() => {
          isUpdatingFieldPositions.current = false;
        }, 100);
      } else {
        showError(`Failed to update field positions: ${response.data?.error || 'Unknown error'}`);
        // Revert to original column order on error
        generateColumns(incubation);
        isUpdatingFieldPositions.current = false;
      }
    } catch (error) {
      showError('Error updating field positions. Please try again.');
      // Revert to original column order on error
      generateColumns(incubation);
      isUpdatingFieldPositions.current = false;
    }
  };

  // Row editing functions
  const handleRowDoubleClick = (rowIndex) => {
    // Check if user has permission to edit participants
    if (!hasIncubationPermission(id, 'edit_participants')) {
      return;
    }
    
    const participant = participants[rowIndex];
    if (!participant) return;
    
    // Prepare edit values for all editable fields
    const values = {};
    
    // Add data fields
    if (participant.incubation_fields) {
      Object.keys(participant.incubation_fields).forEach(fieldName => {
        values[`data_${fieldName}`] = participant.incubation_fields[fieldName] || '';
      });
    }
    
    // Add criteria fields - initialize all criteria from incubation definition
    if (incubation && incubation.inclusion_criteria && Array.isArray(incubation.inclusion_criteria)) {
      incubation.inclusion_criteria.forEach(criteria => {
        const criteriaName = typeof criteria === 'string' ? criteria : criteria.name;
        const criteriaValue = participant.incubation_criteria_fields?.[criteriaName];
        // Handle boolean values and undefined/null cases
        values[`criteria_${criteriaName}`] = criteriaValue === true ? true : criteriaValue === false ? false : null;
      });
    }
    
    // Add exclusion criteria fields - initialize all criteria from incubation definition
    if (incubation && incubation.exclusion_criteria && Array.isArray(incubation.exclusion_criteria)) {
      incubation.exclusion_criteria.forEach(criteria => {
        const criteriaName = typeof criteria === 'string' ? criteria : criteria.name;
        const criteriaValue = participant.incubation_exclusion_fields?.[criteriaName];
        // Handle boolean values and undefined/null cases
        values[`exclusion_${criteriaName}`] = criteriaValue === true ? true : criteriaValue === false ? false : null;
      });
    }
    
    // Add calculated fields - initialize all calculated fields from incubation definition
    if (incubation && incubation.calculated_fields && Array.isArray(incubation.calculated_fields)) {
      incubation.calculated_fields.forEach(field => {
        const fieldName = field.name;
        let fieldValue = participant.calculated_fields?.[fieldName];
        
        // If no stored value, calculate it from source field
        if (!fieldValue && field.source_field_name) {
          const sourceFieldValue = participant.incubation_fields?.[field.source_field_name];
          if (sourceFieldValue) {
            const calculationType = field.calculation_type || 'pet_age';
            if (calculationType === 'pet_age') {
              fieldValue = calculateAge(sourceFieldValue);
            } else if (calculationType === 'lbs_to_kg') {
              fieldValue = convertLbsToKg(sourceFieldValue);
            }
          }
        }
        
        values[`calculated_${fieldName}`] = fieldValue || '';
      });
    }
    
    // Add other editable fields
    Object.keys(participant).forEach(key => {
      if (!['incubation_fields', 'incubation_criteria_fields', 'pet_id', 'pet_incubation_id'].includes(key)) {
        values[key] = participant[key] || '';
      }
    });
    
    setEditingRow(rowIndex);
    setEditValues(values);
    setShowEditModal(true);
  };

  const handleEditValueChange = (fieldKey, value) => {
    setEditValues(prev => {
      const newValues = {
        ...prev,
        [fieldKey]: value
      };

      // Auto-fill Date Of Completion when Completed is set to "Yes"
      if (fieldKey === 'data_completed' && value === 'Yes') {
        // Check if date_of_completion field exists in incubation data_fields
        const hasDateOfCompletion = incubation?.data_fields?.some(
          field => field.name === 'date_of_completion'
        );
        if (hasDateOfCompletion) {
          // Set to current date in MM/DD/YYYY format
          const today = new Date();
          const mm = String(today.getMonth() + 1).padStart(2, '0');
          const dd = String(today.getDate()).padStart(2, '0');
          const yyyy = today.getFullYear();
          newValues['data_date_of_completion'] = `${mm}/${dd}/${yyyy}`;
        }
      }

      // Check if this field is a source for any calculated fields and recalculate them
      if (incubation?.calculated_fields && fieldKey.startsWith('data_')) {
        const sourceFieldName = fieldKey.replace('data_', '');

        incubation.calculated_fields.forEach(calcField => {
          if (calcField.source_field_name === sourceFieldName) {
            const calculationType = calcField.calculation_type || 'pet_age';
            const calculatedKey = `calculated_${calcField.name}`;

            // Perform calculation based on type
            if (calculationType === 'pet_age') {
              newValues[calculatedKey] = calculateAge(value);
            } else if (calculationType === 'lbs_to_kg') {
              newValues[calculatedKey] = convertLbsToKg(value);
            }
          }
        });
      }

      return newValues;
    });
  };

  const handleEditSave = async () => {
    if (editingRow !== null) {
      try {
        setSaving(true);
        setSaveError(null);
        
        const participant   = participantsWithDynamicId[editingRow];
        const participantId = participant.id;
        if (!participant)   { throw new Error('Participant not found') }
        if (!participantId) { throw new Error('Participant missing ID field') }
        
        const requestData       = {};
        const dataFields        = {};
        const inclusionCriteria = {};
        const exclusionCriteria = {};
        const calculatedFields   = {};
        
        Object.keys(editValues).forEach(fieldKey => {
          if (fieldKey.startsWith('data_')) {
            const fieldName = fieldKey.replace('data_', '');
            dataFields[fieldName] = editValues[fieldKey];
          } else if (fieldKey.startsWith('criteria_')) {
            const criteriaName = fieldKey.replace('criteria_', '');
            const criteriaValue = editValues[fieldKey];
            // Only include criteria values that are not null (i.e., true or false)
            if (criteriaValue !== null) {
              inclusionCriteria[criteriaName] = criteriaValue;
            }
          } else if (fieldKey.startsWith('exclusion_')) {
            const criteriaName = fieldKey.replace('exclusion_', '');
            const criteriaValue = editValues[fieldKey];
            // Only include criteria values that are not null (i.e., true or false)
            if (criteriaValue !== null) {
              exclusionCriteria[criteriaName] = criteriaValue;
            }
          } else if (fieldKey.startsWith('calculated_')) {
            const fieldName = fieldKey.replace('calculated_', '');
            calculatedFields[fieldName] = editValues[fieldKey];
          }
        });
        
        if (Object.keys(dataFields).length > 0) {
          requestData.data_fields = dataFields;
        }
        if (Object.keys(inclusionCriteria).length > 0) {
          requestData.inclusion_criteria = inclusionCriteria;
        }
        if (Object.keys(exclusionCriteria).length > 0) {
          requestData.exclusion_criteria = exclusionCriteria;
        }
        if (Object.keys(calculatedFields).length > 0) {
          requestData.calculated_fields = calculatedFields;
        }
        
        const response = await updateParticipant(participantId, requestData);
        
        if (response.data && response.data.success) {
          const newParticipants = [...participants];
          newParticipants[editingRow] = response.data.participant;
          setParticipants(newParticipants);
          
          setEditingRow(null);
          setEditValues({});
          setShowEditModal(false);
        } else {
          throw new Error(response.data?.error || 'Failed to update participant');
        }
      } catch (error) {
        setSaveError(error.message || 'Failed to save changes. Please try again.');
      } finally {
        setSaving(false);
      }
    }
  };

  const handleEditCancel = () => {
    setEditingRow(null);
    setEditValues({});
    setShowEditModal(false);
    setSaveError(null);
  };




  const organizationSelectOptions = organizations.map((org, optionIndex) => ({
    value: org.name,
    label: org.name,
    key: org.organization_id || org.id || `${org.name}-${optionIndex}`
  }));
  const organizationSelectPlaceholder = organizationsLoading
    ? 'Loading organizations...'
    : organizationSelectOptions.length > 0
      ? 'Select organization'
      : 'No organizations available';
  const dashboardUserSelectOptions = dashboardUsers.map((user, optionIndex) => {
    const fullName = `${(user.first_name || '').trim()} ${(user.last_name || '').trim()}`.trim();
    const display = fullName || user.email || `User #${optionIndex + 1}`;
    const organizationName = user.organization?.name || user.organization || '';
    return {
      value: display,
      label: display,
      key: user.id || `${display}-${optionIndex}`,
      organizationName,
      organizationNormalized: normalizeOrgName(organizationName)
    };
  });
  const editSourceValue = editValues['data_source'] || '';
  const editSourceNormalized = editSourceValue ? normalizeOrgName(editSourceValue) : '';
  const filteredDashboardUserSelectOptions = editSourceNormalized
    ? dashboardUserSelectOptions.filter(option => option.organizationNormalized === editSourceNormalized)
    : dashboardUserSelectOptions;
  const dashboardUserSelectPlaceholder = dashboardUsersLoading
    ? 'Loading dashboard users...'
    : filteredDashboardUserSelectOptions.length > 0
      ? (editSourceValue ? `Select contact from ${editSourceValue}` : 'Select contact')
      : (editSourceValue ? `No dashboard users found for ${editSourceValue}` : 'No dashboard users available');

  // Update incubation IDs to use the URL parameter (API already handles pagination)
  
  const participantsWithDynamicId = participants
    .map(participant => ({
      ...participant,
      incubationId: `INC-${id}`
    }));

  // Helper function to calculate age from birth date
  const calculateAge = (birthDate) => {
    if (!birthDate) return '';
    
    try {
      const birth = new Date(birthDate);
      const today = new Date();
      
      // Check if birth date is valid
      if (isNaN(birth.getTime())) return '';
      
      let years = today.getFullYear() - birth.getFullYear();
      let months = today.getMonth() - birth.getMonth();
      
      // Adjust if the birthday hasn't occurred this year
      if (months < 0 || (months === 0 && today.getDate() < birth.getDate())) {
        years--;
        months += 12;
      }
      
      // If birthday is this month but hasn't occurred yet
      if (months === 0 && today.getDate() < birth.getDate()) {
        months = 11;
      }
      
      // Format the result
      if (years === 0) {
        return months === 1 ? '1mo' : `${months}mo`;
      } else if (months === 0) {
        return years === 1 ? '1y' : `${years}y`;
      } else {
        return `${years}y ${months}mo`;
      }
    } catch (error) {
      return '';
    }
  };

  // Helper function to convert lbs to kg
  const convertLbsToKg = (lbs) => {
    if (!lbs || lbs === '') return '';
    
    try {
      const pounds = parseFloat(lbs);
      if (isNaN(pounds)) return '';
      
      // Convert lbs to kg (1 lb = 0.453592 kg)
      const kg = pounds * 0.453592;
      
      // Round to 2 decimal places
      return kg.toFixed(2);
    } catch (error) {
      return '';
    }
  };

  // Get cell value based on column key
  const getCellValue = (participant, columnKey, rowIndex) => {
    // Handle spacer column - return empty content
    if (columnKey === 'spacer') {
      return '';
    }

    // Handle Eligible column - calculated from all inclusion and exclusion criteria
    if (columnKey === 'eligible') {
      // Check all inclusion criteria - all must be true
      const inclusionCriteriaMet = incubation?.inclusion_criteria?.every(criteria => {
        const criteriaName = typeof criteria === 'string' ? criteria : criteria.name;
        return participant.incubation_criteria_fields?.[criteriaName] === true;
      }) ?? true; // Default to true if no inclusion criteria

      // Check all exclusion criteria - all must be false
      const exclusionCriteriaMet = incubation?.exclusion_criteria?.every(criteria => {
        const criteriaName = typeof criteria === 'string' ? criteria : criteria.name;
        return participant.incubation_exclusion_fields?.[criteriaName] === false;
      }) ?? true; // Default to true if no exclusion criteria

      const isEligible = inclusionCriteriaMet && exclusionCriteriaMet;

      if (isEligible) {
        return <i className="fas fa-check" style={{ color: '#10b981', fontSize: '16px' }}></i>;
      } else {
        return <i className="fas fa-times" style={{ color: '#ef4444', fontSize: '16px' }}></i>;
      }
    }

    // Handle locked columns (pet_id, pet_incubation_id) - display in uppercase
    if (columnKey === 'pet_id' || columnKey === 'pet_incubation_id') {
      return (participant[columnKey] || '').toString().toUpperCase();
    }
    
    // Handle data_fields from participant data
    if (columnKey.startsWith('data_')) {
      const fieldName = columnKey.replace('data_', '');
      return participant.incubation_fields?.[fieldName] || '';
    }
    
    // Handle criteria_fields from participant data (boolean values)
    if (columnKey.startsWith('criteria_')) {
      const criteriaName = columnKey.replace('criteria_', '');
      const value = participant.incubation_criteria_fields?.[criteriaName];
      
      if (value === true) {
        return <i className="fas fa-check" style={{ color: '#10b981', fontSize: '16px' }}></i>;
      } else if (value === false) {
        return <i className="fas fa-times" style={{ color: '#ef4444', fontSize: '16px' }}></i>;
      }
      
      return '';
    }
    
    // Handle exclusion_fields from participant data (boolean values)
    if (columnKey.startsWith('exclusion_')) {
      const criteriaName = columnKey.replace('exclusion_', '');
      const value = participant.incubation_exclusion_fields?.[criteriaName];
      
      if (value === true) {
        return <i className="fas fa-check" style={{ color: '#10b981', fontSize: '16px' }}></i>;
      } else if (value === false) {
        return <i className="fas fa-times" style={{ color: '#ef4444', fontSize: '16px' }}></i>;
      }
      
      return '';
    }
    
    // Handle calculated_fields from participant data
    if (columnKey.startsWith('calculated_')) {
      const fieldName = columnKey.replace('calculated_', '');
      
      // Get the column metadata to determine calculation type
      const column = columnOrder.find(col => col.key === columnKey);
      
      if (column?.sourceFieldName && column?.calculationType) {
        // Get the source field value from participant data
        const sourceFieldValue = participant.incubation_fields?.[column.sourceFieldName];
        
        // Perform calculation based on type if source value exists
        if (column.calculationType === 'pet_age' && sourceFieldValue) {
          return calculateAge(sourceFieldValue);
        } else if (column.calculationType === 'lbs_to_kg' && sourceFieldValue) {
          return convertLbsToKg(sourceFieldValue);
        }
        
        // If source field doesn't have a value, return empty
        return '';
      }
      
      // For other calculated fields, return the stored value
      return participant.calculated_fields?.[fieldName] || '';
    }
    
    // Handle status column if it exists
    if (columnKey === 'status') {
      return (
        <span className={`incubation-status-badge ${participant[columnKey]?.toLowerCase() || ''}`}>
          {participant[columnKey] || ''}
        </span>
      );
    }
    
    return participant[columnKey] || '';
  };

  // Check if user has access to this incubation
  const checkIncubationAccess = () => {
    // Check if user has admin or manage dashboard permissions
    const isAdmin = isDashboardAdmin();
    const isManager = hasGlobalFunctionality('manage_dashboard');
    
    // If user has admin or manage permissions, allow access
    if (isAdmin || isManager) {
      return true;
    }
    
    // Otherwise, check if user has access to this specific incubation
    return hasIncubationAccess(id);
  };

  // If user doesn't have access to this incubation, show access denied
  if (id && !checkIncubationAccess()) {
    return (
      <div className="incubation-screen">
        <div className="incubation-header-container">
          <div className="incubation-header-left">
            <button className="incubation-back-button" onClick={() => navigate(-1)}>
              <i className="fas fa-arrow-left"></i>
            </button>
            <div className="incubation-header">
              <h1 className="incubation-title">Access Denied</h1>
            </div>
          </div>
        </div>
        
        <div className="incubation-content">
          <Card style={{ width: '700px', margin: '0 auto', padding: '20px', overflow: 'visible' }}>
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <i className="fas fa-lock" style={{ fontSize: '48px', marginBottom: '20px', color: '#6b7280' }}></i>
              <h2 style={{ marginBottom: '16px', color: '#374151' }}>Access Denied</h2>
              <p style={{ color: '#6b7280', fontSize: '16px', lineHeight: '1.5' }}>
                You don't have access to this incubation.
              </p>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="incubation-screen">
      <div className="incubation-header-container">
        <div className="incubation-header-left">
          <button className="incubation-back-button" onClick={handleBackClick}>
            <i className="fas fa-arrow-left"></i>
          </button>
          <div className="incubation-header">
            {!incubationLoading && incubation?.name && (
              <div className="incubation-title-group">
                <div className="incubation-eyebrow-row">
                  <span className="incubation-eyebrow">Incubation</span>
                  <span className="incubation-eyebrow-separator">|</span>
                  <span
                    className="incubation-view-details-link"
                    role="button"
                    tabIndex={0}
                    onClick={handleViewDetailsClick}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleViewDetailsClick();
                      }
                    }}
                  >
                    View Details
                  </span>
                </div>
                <h1 className="incubation-title animate-in" onClick={handleViewDetailsClick} title="View Details">{incubation.name}</h1>
              </div>
            )}
          </div>
        </div>
        <div className="incubation-header-actions">
          <Popover content="Sort & Filter" trigger="hover" placement="top" compact>
            <Button variant="outline" size="small" className="config-button" onClick={handleConfigClick}>
              <i className="fas fa-sliders-h"></i>
            </Button>
          </Popover>
          <div style={{ width: '1px', height: '24px', backgroundColor: '#e5e7eb', margin: '0 8px' }}></div>
          {hasIncubationPermission(id, 'edit_incubations') && (
            <Popover content="Surveys" trigger="hover" placement="top" compact>
              <Button variant="outline" size="small" className="surveys-button" onClick={handleSurveysClick}>
                <i className="fas fa-poll-h" style={{ color: '#0054a4', display: 'inline-block', fontSize: '16px' }}></i>
              </Button>
            </Popover>
          )}
          {hasIncubationPermission(id, 'view_webhook_details') && (
            <Popover content="Webhook Instructions" trigger="hover" placement="top" compact>
              <Button variant="outline" size="small" className="webhook-button" onClick={handleWebhookInstructionsClick}>
                <i className="fas fa-code-branch" style={{ color: '#0054a4', display: 'inline-block', fontSize: '16px' }}></i>
              </Button>
            </Popover>
          )}
          {hasIncubationPermission(id, 'add_participants') && (
            <Popover content="Import CSV Participants" trigger="hover" placement="top" compact>
              <Button variant="outline" size="small" className="csv-button" onClick={handleAddParticipantsCsvClick}>
                <i className="fas fa-file-csv" style={{ color: '#0054a4', display: 'inline-block', fontSize: '16px' }}></i>
              </Button>
            </Popover>
          )}
          {hasIncubationPermission(id, 'add_participants') && (
            <Popover content="Add Participant(s)" trigger="hover" placement="top" compact>
              <Button variant="outline" size="small" className="add-participant-button" onClick={handleAddParticipantsClick}>
                <i className="fas fa-user-plus" style={{ color: '#0054a4', display: 'inline-block', fontSize: '16px' }}></i>
              </Button>
            </Popover>
          )}
        </div>
      </div>
      
      {/* <div className="incubation-tool-buttons">
        <div className='incubation-tool-buttons-panel'>
          <div style={{ width: '1px', height: '24px', backgroundColor: '#e5e7eb', margin: '0 12px' }}></div>
          <button className="incubation-settings-button" title="Settings">
            <i className="fas fa-sliders-h"></i>
          </button>
        </div>
      </div> */}
      
      <div className="incubation-content">
        {/* Configuration Panel */}
        {showConfigPanel && (
          <div className="config-panel">
            <div className="config-panel-content">
              <div className="config-field">
                <label className="config-label">Sort Order</label>
                <select 
                  value={sortOrder} 
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="config-select"
                >
                  <option value="asc">Ascending</option>
                  <option value="desc">Descending</option>
                </select>
              </div>
              <div className="config-field">
                <label className="config-label">Sort By Column</label>
                <select 
                  value={sortColumn} 
                  onChange={(e) => setSortColumn(e.target.value)}
                  className="config-select"
                >
                  <option value="">Select a column</option>
                  {columnOrder
                    .filter(column => !column.isSpacer && column.key !== 'spacer')
                    .map(column => (
                      <option key={column.key} value={column.key}>
                        {column.label}
                      </option>
                    ))}
                </select>
              </div>
              <div className="config-field">
                <label className="config-label">Filter by Column</label>
                <select 
                  value={filterColumn} 
                  onChange={(e) => setFilterColumn(e.target.value)}
                  className="config-select"
                >
                  <option value="">Select a column</option>
                  {columnOrder
                    .filter(column => !column.isSpacer && column.key !== 'spacer')
                    .map(column => (
                      <option key={column.key} value={column.key}>
                        {column.label}
                      </option>
                    ))}
                </select>
              </div>
              <div className="config-field">
                <label className="config-label">Filter Value</label>
                <input 
                  type="text"
                  value={filterValue} 
                  onChange={(e) => setFilterValue(e.target.value)}
                  className="config-input"
                  placeholder="Enter filter value"
                />
              </div>
              <div className="config-panel-actions">
                <Button variant="outline" onClick={handleClear}>
                  Clear
                </Button>
                <Button variant="primary" onClick={handleSortSubmit} disabled={!sortColumn && !(filterColumn && filterValue.trim())}>
                  Apply
                </Button>
              </div>
            </div>
          </div>
        )}
        
        {!incubationLoading && !loading && (
        <Card className="incubation-participants-card allow-overflow">
          <div className="incubation-participants-table-container">
            {!incubationLoading && !loading && (
              <table className={`incubation-participants-table ${deleteMode ? 'delete-mode' : ''}`}>
                <thead>
                  <tr>
                    {deleteMode && (
                      <th className="column-header checkbox-header">
                        <input
                          type="checkbox"
                          onChange={handleSelectAll}
                          checked={participantsWithDynamicId.length > 0 && selectedParticipants.size === participantsWithDynamicId.length}
                          className="participant-checkbox"
                        />
                      </th>
                    )}
                    {columnOrder.map((column, index) => (
                      <th
                        key={column.key}
                        draggable={!column.locked && !column.isSpacer && !deleteMode}
                        onDragStart={!column.isSpacer && !deleteMode ? (e) => handleDragStart(e, index) : undefined}
                        onDragOver={!column.isSpacer && !deleteMode ? (e) => handleDragOver(e, index) : undefined}
                        onDragLeave={!column.isSpacer && !deleteMode ? (e) => handleDragLeave(e, index) : undefined}
                        onDrop={!column.isSpacer && !deleteMode ? (e) => handleDrop(e, index) : undefined}
                        onDragEnd={!column.isSpacer && !deleteMode ? handleDragEnd : undefined}
                        className={`column-header ${column.locked ? 'locked' : column.isSpacer ? 'spacer' : 'draggable'} ${column.key && column.key.startsWith('criteria_') ? 'inclusion-criteria-header' : ''} ${column.key && column.key.startsWith('exclusion_') ? 'exclusion-criteria-header' : ''} ${column.key && column.key.startsWith('data_') && column.fieldType === 'yes_no' ? 'yes-no-header' : ''} ${column.key === 'eligible' ? 'eligible-header' : ''}`.trim()}
                        data-column-index={index}
                      >
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {participantsWithDynamicId.length === 0 ? (
                    <tr>
                      <td colSpan={columnOrder.length + (deleteMode ? 1 : 0)} style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                        No participants found
                      </td>
                    </tr>
                  ) : (
                    participantsWithDynamicId.map((participant, rowIndex) => (
                      <tr
                        key={rowIndex}
                        className={selectedParticipants.has(participant.id) ? 'selected-row' : ''}
                      >
                        {deleteMode && (
                          <td className="checkbox-cell">
                            <input
                              type="checkbox"
                              checked={selectedParticipants.has(participant.id)}
                              onChange={(e) => handleCheckboxChange(participant.id, rowIndex, e)}
                              onClick={(e) => e.stopPropagation()}
                              className="participant-checkbox"
                            />
                          </td>
                        )}
                        {columnOrder.map((column, colIndex) => (
                          <td
                            key={`${rowIndex}-${column.key}`}
                            className={`${column.locked ? 'locked' : column.isSpacer ? 'spacer' : ''} ${column.key && column.key.startsWith('criteria_') ? 'criteria-cell' : ''} ${column.key && column.key.startsWith('exclusion_') ? 'exclusion-criteria-cell' : ''} ${column.key && column.key.startsWith('data_') && column.fieldType === 'yes_no' ? 'yes-no-cell' : ''} ${column.key === 'eligible' ? 'eligible-cell' : ''} ${column.locked && lockedHoverRow === rowIndex ? 'locked-row-hover' : ''}`.trim()}
                            onDoubleClick={!deleteMode && !column.isSpacer && (!column.locked || column.key.startsWith('criteria_') || column.key.startsWith('exclusion_')) && hasIncubationPermission(id, 'edit_participants') ? () => handleRowDoubleClick(rowIndex) : undefined}
                            onClick={!deleteMode && column.locked && !column.key.startsWith('criteria_') && !column.key.startsWith('exclusion_') && hasIncubationPermission(id, 'view_pet_details') ? (e) => {
                              if (!e.shiftKey && (column.key === 'pet_id' || column.key === 'pet_incubation_id') && participant.pet_incubation_id) {
                                navigate(`/participant-details/${participant.pet_incubation_id}`);
                              } else {
                                navigate(`/pet-details/${participant.pet_id}`);
                              }
                            } : undefined}
                            onMouseEnter={column.locked && !column.key.startsWith('criteria_') && !column.key.startsWith('exclusion_') ? () => setLockedHoverRow(rowIndex) : undefined}
                            onMouseLeave={column.locked && !column.key.startsWith('criteria_') && !column.key.startsWith('exclusion_') ? () => setLockedHoverRow(null) : undefined}
                            style={{
                              cursor: !deleteMode && ((column.locked && !column.key.startsWith('criteria_') && !column.key.startsWith('exclusion_') && hasIncubationPermission(id, 'view_pet_details')) || ((column.key.startsWith('criteria_') || column.key.startsWith('exclusion_')) && hasIncubationPermission(id, 'edit_participants'))) ? 'pointer' : 'default'
                            }}
                          >
                            {column.isSpacer ? null : (
                              (() => {
                                const content = getCellValue(participant, column.key, rowIndex);
                                return (typeof content === 'string' || typeof content === 'number')
                                  ? <span className="cell-text">{content}</span>
                                  : content;
                              })()
                            )}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>

          {!incubationLoading && !loading && (
            <div className="incubation-pagination-container">
              <div className="incubation-pagination-left">
                {!deleteMode ? (
                  <>
                    {hasIncubationPermission(id, 'view_incubation_logs') && (
                      <Button variant="outline" size="small" className="logs-button" onClick={handleLogsClick}>
                        <i className="fas fa-file-alt"></i>
                        Logs
                      </Button>
                    )}
                    {hasIncubationPermission(id, 'download_incubation_data') && (
                      <DownloadMenu
                        onDownload={handleDownloadParticipants}
                        busy={exporting}
                      />
                    )}
                    {/* Delete Participants button - commented out for now, will reintroduce later
                    {hasIncubationPermission(id, 'edit_participants') && (
                      <Button variant="outline" size="small" className="delete-participants-button" onClick={handleEnterDeleteMode}>
                        <i className="fas fa-trash-alt"></i>
                        Delete Participants
                      </Button>
                    )}
                    */}
                  </>
                ) : (
                  <>
                    <Button variant="outline" size="small" className="cancel-delete-button" onClick={handleCancelDeleteMode} disabled={deleting}>
                      Cancel
                    </Button>
                    <Button
                      variant="danger"
                      size="small"
                      className="delete-selected-button"
                      onClick={handleDeleteSelected}
                      disabled={selectedParticipants.size === 0 || deleting}
                    >
                      {deleting ? 'Deleting...' : `Delete Selected (${selectedParticipants.size})`}
                    </Button>
                  </>
                )}
              </div>
              <div className="incubation-pagination-right">
                <div className="items-per-page-container">
                  <select
                    value={itemsPerPage}
                    onChange={handleItemsPerPageChange}
                    className="items-per-page-select"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span className="items-per-page-label">Per Page</span>
                </div>
                <div className="incubation-pagination-controls">
                  <button
                    className="incubation-pagination-button"
                    onClick={handlePreviousPage}
                    disabled={!hasPrevious}
                  >
                    <i className="fas fa-chevron-left"></i>
                  </button>
                  <span className="incubation-pagination-info">
                    Page {currentPage}
                  </span>
                  <button
                    className="incubation-pagination-button"
                    onClick={handleNextPage}
                    disabled={!hasMore}
                  >
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              </div>
            </div>
          )}
        </Card>
        )}

        {/* Edit Modal - Rendered at root level to avoid z-index issues */}
        {showEditModal && hasIncubationPermission(id, 'edit_participants') && (
          <div className="edit-modal-overlay" onClick={handleEditCancel}>
            <div className="edit-modal" onClick={(e) => e.stopPropagation()}>
              <div className="edit-modal-header">
                <h3>Edit Participant Data</h3>
                <button className="edit-modal-close" onClick={handleEditCancel}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="edit-modal-content">
                {saveError && (
                  <div className="edit-modal-error">
                    <i className="fas fa-exclamation-triangle"></i>
                    <span>{saveError}</span>
                  </div>
                )}
                {columnOrder
                  .filter(column => (!column.locked || column.key.startsWith('criteria_') || column.key.startsWith('exclusion_')) && !column.isSpacer && column.key !== 'spacer')
                  .map(column => {
                    const fieldKey = column.key;
                    // Handle criteria fields specially - preserve null values for empty state
                    const currentValue = fieldKey.startsWith('criteria_') || fieldKey.startsWith('exclusion_')
                      ? editValues[fieldKey]
                      : editValues[fieldKey] || '';

                    return (
                      <div key={fieldKey} className="edit-modal-field">
                        <label className="edit-modal-label">
                          {column.label}
                        </label>
                        {fieldKey === 'status' ? (
                          <select
                            value={currentValue}
                            onChange={(e) => handleEditValueChange(fieldKey, e.target.value)}
                            className="edit-modal-select"
                          >
                            <option value="Active">Active</option>
                            <option value="Pending">Pending</option>
                          </select>
                        ) : fieldKey === 'data_source' ? (
                          <select
                            value={currentValue}
                            onChange={(e) => handleEditValueChange(fieldKey, e.target.value)}
                            className="edit-modal-select"
                            disabled={organizationsLoading}
                          >
                            <option value="">{organizationSelectPlaceholder}</option>
                            {organizationSelectOptions.map(option => (
                              <option key={option.key} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        ) : fieldKey === 'data_source_contact' ? (
                          <select
                            value={currentValue}
                            onChange={(e) => handleEditValueChange(fieldKey, e.target.value)}
                            className="edit-modal-select"
                            disabled={dashboardUsersLoading}
                          >
                            <option value="">{dashboardUserSelectPlaceholder}</option>
                            {filteredDashboardUserSelectOptions.map(option => (
                              <option key={option.key} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        ) : fieldKey.startsWith('criteria_') || fieldKey.startsWith('exclusion_') ? (
                          <select
                            value={currentValue === true ? 'true' : currentValue === false ? 'false' : ''}
                            onChange={(e) => handleEditValueChange(fieldKey, e.target.value === '' ? null : e.target.value === 'true')}
                            className="edit-modal-select"
                          >
                            <option value="">Select an option</option>
                            <option value="true">Yes</option>
                            <option value="false">No</option>
                          </select>
                        ) : fieldKey.startsWith('data_') && column.fieldType === 'choices' && column.choices ? (
                          <select
                            value={currentValue}
                            onChange={(e) => handleEditValueChange(fieldKey, e.target.value)}
                            className="edit-modal-select"
                          >
                            <option value="">Select an option</option>
                            {column.choices.map((choice, index) => (
                              <option key={index} value={choice}>
                                {choice}
                              </option>
                            ))}
                          </select>
                        ) : fieldKey.startsWith('data_') && column.fieldType === 'yes_no' ? (
                          <select
                            value={currentValue}
                            onChange={(e) => handleEditValueChange(fieldKey, e.target.value)}
                            className="edit-modal-select"
                          >
                            <option value="">Select an option</option>
                            <option value="Yes">Yes</option>
                            <option value="No">No</option>
                          </select>
                        ) : fieldKey.startsWith('calculated_') ? (
                          <input
                            type="text"
                            value={currentValue}
                            onChange={(e) => handleEditValueChange(fieldKey, e.target.value)}
                            className="edit-modal-input"
                            placeholder={`Enter ${column.label.toLowerCase()}`}
                            readOnly
                            style={{ backgroundColor: '#f3f4f6', color: '#6b7280' }}
                          />
                        ) : (() => {
                          // Check if this is the Owner State field
                          const isOwnerStateField = column.label === 'Owner State' ||
                                              (fieldKey.startsWith('data_') && fieldKey.replace('data_', '') === displayStringToKey('Owner State'));

                          if (isOwnerStateField) {
                            const usStates = get_us_states();
                            // Find the timezone field key
                            const timezoneColumn = columnOrder.find(col =>
                              col.label === 'Owner Timezone' ||
                              (col.key.startsWith('data_') && col.key.replace('data_', '') === displayStringToKey('Owner Timezone'))
                            );
                            const timezoneFieldKey = timezoneColumn?.key;

                            const handleStateChangeEdit = (stateValue) => {
                              handleEditValueChange(fieldKey, stateValue);
                              // Auto-update timezone when state changes
                              if (timezoneFieldKey) {
                                const timezone = get_timezone_for_state(stateValue);
                                const timezoneDisplay = get_timezone_display(timezone);
                                handleEditValueChange(timezoneFieldKey, timezoneDisplay);
                              }
                            };

                            return (
                              <select
                                value={currentValue || ''}
                                onChange={(e) => handleStateChangeEdit(e.target.value)}
                                className="edit-modal-select"
                              >
                                <option value="">Select state</option>
                                {usStates.map((state, stateIndex) => (
                                  <option key={stateIndex} value={state.value}>
                                    {state.label}
                                  </option>
                                ))}
                              </select>
                            );
                          }

                          // Check if this is the Owner Timezone field (read-only, auto-populated from state)
                          const isOwnerTimezoneField = column.label === 'Owner Timezone' ||
                                              (fieldKey.startsWith('data_') && fieldKey.replace('data_', '') === displayStringToKey('Owner Timezone'));

                          if (isOwnerTimezoneField) {
                            return (
                              <input
                                type="text"
                                value={currentValue || ''}
                                readOnly
                                disabled
                                className="edit-modal-input"
                                placeholder="Auto-filled from state"
                                style={{ backgroundColor: '#f3f4f6', color: '#6b7280' }}
                              />
                            );
                          }

                          // Check if this is a phone number field
                          const isPhoneField = column.label === 'Owner Phone Number' ||
                                              column.label === 'Owner Phone Number - Alternate' ||
                                              (fieldKey.startsWith('data_') && fieldKey.replace('data_', '') === displayStringToKey('Owner Phone Number')) ||
                                              (fieldKey.startsWith('data_') && fieldKey.replace('data_', '') === displayStringToKey('Owner Phone Number - Alternate'));

                          if (isPhoneField) {
                            // Format phone number for display: (555) 555-5555
                            const formatPhoneForDisplay = (digits) => {
                              if (!digits) return '';
                              // Ensure we only have digits
                              const cleanDigits = digits.replace(/\D/g, '').slice(0, 10);

                              if (cleanDigits.length === 0) return '';
                              if (cleanDigits.length <= 3) return `(${cleanDigits}`;
                              if (cleanDigits.length <= 6) return `(${cleanDigits.slice(0, 3)}) ${cleanDigits.slice(3)}`;
                              return `(${cleanDigits.slice(0, 3)}) ${cleanDigits.slice(3, 6)}-${cleanDigits.slice(6)}`;
                            };

                            const handlePhoneChangeEdit = (value) => {
                              // Store only digits
                              const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
                              handleEditValueChange(fieldKey, digitsOnly);
                            };

                            // Get stored digits and format for display
                            const storedDigits = currentValue || '';
                            const displayValue = formatPhoneForDisplay(storedDigits);

                            return (
                              <input
                                type="tel"
                                value={displayValue}
                                onChange={(e) => handlePhoneChangeEdit(e.target.value)}
                                className="edit-modal-input"
                                placeholder="(555) 555-5555"
                                maxLength={14}
                              />
                            );
                          }

                          // Check if this is a date picker field (consent date or pet birthdate)
                          const isDatePickerField = column.label === 'Owner consent form - date fully signed' ||
                                              column.label === 'Pet Birthdate - Reported' ||
                                              (fieldKey.startsWith('data_') && fieldKey.replace('data_', '') === displayStringToKey('Owner consent form - date fully signed')) ||
                                              (fieldKey.startsWith('data_') && fieldKey.replace('data_', '') === displayStringToKey('Pet Birthdate - Reported'));

                          if (isDatePickerField) {
                            // Calculate date constraints
                            const today = new Date();
                            const maxDate = today.toISOString().split('T')[0]; // Today (no future dates)
                            const minDate = new Date(today.getFullYear() - 30, today.getMonth(), today.getDate()).toISOString().split('T')[0]; // 30 years ago

                            // Check if this is specifically the Pet Birthdate field
                            const isPetBirthdateField = column.label === 'Pet Birthdate - Reported' ||
                                                (fieldKey.startsWith('data_') && fieldKey.replace('data_', '') === displayStringToKey('Pet Birthdate - Reported'));

                            // Convert MM/DD/YYYY to YYYY-MM-DD for date input
                            const toInputFormat = (mmddyyyy) => {
                              if (!mmddyyyy) return '';
                              const parts = mmddyyyy.split('/');
                              if (parts.length !== 3) return '';
                              const [mm, dd, yyyy] = parts;
                              return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
                            };

                            // Convert YYYY-MM-DD to MM/DD/YYYY for storage
                            const toStorageFormat = (yyyymmdd) => {
                              if (!yyyymmdd) return '';
                              const parts = yyyymmdd.split('-');
                              if (parts.length !== 3) return '';
                              const [yyyy, mm, dd] = parts;
                              return `${mm}/${dd}/${yyyy}`;
                            };

                            // Calculate age from birthdate and check if over 30 years
                            const calculateAge = (mmddyyyy) => {
                              if (!mmddyyyy) return null;
                              const parts = mmddyyyy.split('/');
                              if (parts.length !== 3) return null;
                              const [mm, dd, yyyy] = parts;
                              const birthDate = new Date(parseInt(yyyy), parseInt(mm) - 1, parseInt(dd));
                              const ageDiff = today - birthDate;
                              const ageDate = new Date(ageDiff);
                              return Math.abs(ageDate.getUTCFullYear() - 1970);
                            };

                            const handleDateChangeEdit = (value) => {
                              const storageValue = toStorageFormat(value);
                              handleEditValueChange(fieldKey, storageValue);
                            };

                            // Open date picker when clicking anywhere on the input
                            const handleDateClickEdit = (e) => {
                              try {
                                e.target.showPicker();
                              } catch (err) {
                                // showPicker() may not be supported in all browsers
                              }
                            };

                            const storedValue = currentValue || '';
                            const inputValue = toInputFormat(storedValue);

                            // Check for age validation error (only for Pet Birthdate)
                            const petAge = isPetBirthdateField ? calculateAge(storedValue) : null;
                            const ageValidationError = isPetBirthdateField && petAge !== null && petAge > 30
                              ? 'Birthday is invalid, pet cannot be older than 30 years.'
                              : null;

                            return (
                              <div>
                                <input
                                  type="date"
                                  value={inputValue}
                                  onChange={(e) => handleDateChangeEdit(e.target.value)}
                                  onClick={handleDateClickEdit}
                                  min={minDate}
                                  max={maxDate}
                                  className={`edit-modal-input ${ageValidationError ? 'error' : ''}`}
                                  style={{ cursor: 'pointer' }}
                                />
                                {ageValidationError && (
                                  <span className="error-message" style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                                    {ageValidationError}
                                  </span>
                                )}
                              </div>
                            );
                          }

                          // Check if this is a breed field (primary or secondary) and pet_type is 'cat' or 'dog'
                          const isPrimaryBreedField = column.label === 'Pet Breed Reported - Primary' ||
                                              column.label === 'Pet Primary Breed - Reported' ||
                                              (fieldKey.startsWith('data_') && fieldKey.replace('data_', '') === displayStringToKey('Pet Breed Reported - Primary')) ||
                                              (fieldKey.startsWith('data_') && fieldKey.replace('data_', '') === displayStringToKey('Pet Primary Breed - Reported'));
                          const isSecondaryBreedField = column.label === 'Pet Breed Reported - Secondary' ||
                                              (fieldKey.startsWith('data_') && fieldKey.replace('data_', '') === displayStringToKey('Pet Breed Reported - Secondary'));
                          const isBreedField = isPrimaryBreedField || isSecondaryBreedField;

                          if (isBreedField) {
                            // Find pet_type field value
                            const petTypeColumn = columnOrder.find(col =>
                              col.label === 'Pet Type' ||
                              (col.key.startsWith('data_') && col.key.replace('data_', '') === 'pet_type')
                            );
                            const petTypeFieldKey = petTypeColumn?.key;
                            const petTypeValue = petTypeFieldKey ? (editValues[petTypeFieldKey] || '') : '';
                            const petTypeLower = petTypeValue ? petTypeValue.toLowerCase() : '';
                            const isCat = petTypeLower === 'cat';
                            const isDog = petTypeLower === 'dog';

                            if (isCat) {
                              const catBreeds = get_cat_breeds();
                              // For secondary breed, add 'None' at the beginning
                              const breedList = isSecondaryBreedField ? ['None', ...catBreeds] : catBreeds;
                              return (
                                <select
                                  value={currentValue || (isSecondaryBreedField ? 'None' : '')}
                                  onChange={(e) => handleEditValueChange(fieldKey, e.target.value)}
                                  className="edit-modal-select"
                                >
                                  <option value="">{isSecondaryBreedField ? "Select secondary cat breed" : "Select cat breed"}</option>
                                  {breedList.map((breed, breedIndex) => (
                                    <option key={breedIndex} value={breed}>
                                      {breed}
                                    </option>
                                  ))}
                                </select>
                              );
                            }

                            if (isDog) {
                              const dogBreeds = get_dog_breeds();
                              // For secondary breed, add 'None' at the beginning
                              const breedList = isSecondaryBreedField ? ['None', ...dogBreeds] : dogBreeds;
                              return (
                                <select
                                  value={currentValue || (isSecondaryBreedField ? 'None' : '')}
                                  onChange={(e) => handleEditValueChange(fieldKey, e.target.value)}
                                  className="edit-modal-select"
                                >
                                  <option value="">{isSecondaryBreedField ? "Select secondary dog breed" : "Select dog breed"}</option>
                                  {breedList.map((breed, breedIndex) => (
                                    <option key={breedIndex} value={breed}>
                                      {breed}
                                    </option>
                                  ))}
                                </select>
                              );
                            }
                          }

                          return (
                            <input
                              type="text"
                              value={currentValue}
                              onChange={(e) => handleEditValueChange(fieldKey, e.target.value)}
                              className="edit-modal-input"
                              placeholder={`Enter ${column.label.toLowerCase()}`}
                            />
                          );
                        })()}
                      </div>
                    );
                  })}
              </div>
              <div className="edit-modal-actions">
                <div className="right-actions" style={{ marginLeft: 'auto' }}>
                  <Button variant="outline" onClick={handleEditCancel} disabled={saving}>
                    Cancel
                  </Button>
                  <Button variant="primary" onClick={handleEditSave} loading={saving} disabled={saving}>
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* All Engagements Table */}
        {!incubationLoading && !loading && (
          <Card className="incubation-participants-card engagements-card" style={{ marginTop: '24px' }}>
            <div className="engagements-title-header">
              <h3>Engagements</h3>
            </div>
            <div className="incubation-participants-table-container">
              {engagementsLoading ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading engagements...</div>
              ) : allEngagements.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>No engagements found</div>
              ) : (
                <table className="incubation-participants-table">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Engagement</th>
                      <th>Pet Name</th>
                      <th>Engagement Type</th>
                      <th>Duration</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allEngagements.map((engagement, index) => {
                      const email = engagement.email || 'N/A';
                      const engagementText = formatEngagementText(engagement.subject || engagement.category || '');
                      const petName = engagement.patient?.name || 'Unknown';
                      const engagementType = formatConsultationType(engagement.type || '');
                      const date = engagement.created_at
                        ? new Date(engagement.created_at).toLocaleDateString()
                        : 'N/A';
                      const isChat = (engagement.type || '').toUpperCase() === 'CHAT';
                      const duration = isChat && engagement.created_at && engagement.resolved_at
                        ? `${Math.round((new Date(engagement.resolved_at) - new Date(engagement.created_at)) / 60000)} min`
                        : '';
                      const isClickable = ['CHAT', 'THREAD'].includes((engagement.type || '').toUpperCase());

                      return (
                        <tr
                          key={engagement._id || engagement.e_id || index}
                          onClick={() => handleEngagementRowClick(engagement)}
                          style={{ cursor: isClickable ? 'pointer' : 'default' }}
                        >
                          <td>{email}</td>
                          <td>{engagementText}</td>
                          <td>{petName}</td>
                          <td>{engagementType}</td>
                          <td>{duration}</td>
                          <td>{date}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {!engagementsLoading && allEngagements.length > 0 && (
              <div className="incubation-pagination-container">
                <div className="incubation-pagination-left">
                  {hasIncubationPermission(id, 'download_incubation_data') && (
                    <DownloadMenu
                      onDownload={handleDownloadEngagements}
                      busy={engagementsExporting}
                      placement="top"
                    />
                  )}
                </div>
                <div className="incubation-pagination-right">
                  <div className="items-per-page-container">
                    <select
                      value={engagementsItemsPerPage}
                      onChange={(e) => {
                        setEngagementsItemsPerPage(Number(e.target.value));
                        setEngagementsPage(1);
                      }}
                      className="items-per-page-select"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span className="items-per-page-label">Per Page</span>
                  </div>
                  <div className="incubation-pagination-controls">
                    <button
                      className="incubation-pagination-button"
                      onClick={() => setEngagementsPage(p => p - 1)}
                      disabled={!engagementsPagination.has_previous}
                    >
                      <i className="fas fa-chevron-left"></i>
                    </button>
                    <span className="incubation-pagination-info">
                      Page {engagementsPagination.page || 1}
                    </span>
                    <button
                      className="incubation-pagination-button"
                      onClick={() => setEngagementsPage(p => p + 1)}
                      disabled={!engagementsPagination.has_next}
                    >
                      <i className="fas fa-chevron-right"></i>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Chat History Modal */}
        {showChatModal && (
          <div className="modal-overlay" role="dialog" aria-modal="true" onClick={() => setShowChatModal(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="modal-title">Consultation Details</h3>
                <button className="modal-close-button" onClick={() => setShowChatModal(false)} aria-label="Close">
                  ×
                </button>
              </div>
              <div className="modal-body">
                <div className="pet-data-grid">
                  <div className="pet-data-item">
                    <div className="pet-data-row">
                      <div className="pet-data-field-header">
                        <strong className="clickable-field-label">Pet Name:</strong>
                      </div>
                      <span className="pet-data-value">{selectedEngagement?.patient?.name || 'N/A'}</span>
                    </div>
                  </div>
                  <div className="pet-data-item">
                    <div className="pet-data-row">
                      <div className="pet-data-field-header">
                        <strong className="clickable-field-label">Breed:</strong>
                      </div>
                      <span className="pet-data-value">{selectedEngagement?.patient?.breed || 'N/A'}</span>
                    </div>
                  </div>
                  <div className="pet-data-item">
                    <div className="pet-data-row">
                      <div className="pet-data-field-header">
                        <strong className="clickable-field-label">Category:</strong>
                      </div>
                      <span className="pet-data-value">{formatEngagementText(selectedEngagement?.category || 'N/A')}</span>
                    </div>
                  </div>
                  <div className="pet-data-item">
                    <div className="pet-data-row">
                      <div className="pet-data-field-header">
                        <strong className="clickable-field-label">Resolved Date:</strong>
                      </div>
                      <span className="pet-data-value">{selectedEngagement?.resolved_at ? new Date(selectedEngagement.resolved_at).toLocaleString() : 'N/A'}</span>
                    </div>
                  </div>
                </div>

                <div className="modal-chat-section">
                  <h4 className="modal-chat-title">Chat Transcript</h4>
                  {chatLoading && (
                    <div className="chat-loading">
                      <i className="fas fa-spinner fa-spin"></i>
                      <span>Loading chat messages...</span>
                    </div>
                  )}
                  {!chatLoading && chatMessages.length === 0 && (
                    <div className="chat-empty">No chat messages found.</div>
                  )}
                  {!chatLoading && chatMessages.length > 0 && (
                    <div className="chat-messages">
                      {chatMessages.map((msg, i) => (
                        <div key={i} className={`chat-message ${msg.sender === 'provider' ? 'from-provider' : 'from-client'}`}>
                          <div className="chat-message-meta">
                            <span className="chat-author">{msg.sender === 'provider' ? 'Provider' : 'Client'}</span>
                          </div>
                          <div className="chat-content">{msg.message}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IncubationScreen;
